import { parseDate } from '@internationalized/date';
import { useState } from 'react';
import { FormError } from 'shared/components/FormControl';

import { CaseDataManager } from 'dashboard/models/case';
import { ModuleInput } from 'dashboard/modules/Input/model';
import Card from 'design-system/components/Card';
import DatePicker from 'design-system/components/DatePicker';
import { default as InputComponent } from 'design-system/components/Input';
import { Label, Paragraph } from 'design-system/components/Text';
import { FieldSchema, FieldSchemaType } from 'lib/fields';

export interface InputProps {
  module: ModuleInput;
  fieldSchemas: FieldSchema[];
  caseDataManager: CaseDataManager;
  isPreview: boolean;
  updateCaseField: (fieldsToUpdate: Record<string, any>) => void;
}

const Input = ({
  module,
  fieldSchemas,
  caseDataManager,
  isPreview = false,
  updateCaseField,
}: InputProps) => {
  const [fieldValue, setFieldValue] = useState<unknown>(
    caseDataManager.case ? caseDataManager.case.fields[module['field-key']] : '',
  );

  const handleOnBlur: () => void = () => {
    if (isPreview) {
      return null;
    } else {
      updateCaseField({ [module['field-key']]: fieldValue });
    }
  };

  const fieldSchema = fieldSchemas.find(
    (schema) => schema.key === module['field-key'].split('-').join('_'),
  );

  return (
    <Card data-test={`module__input--${module.token}`}>
      <Card.Body>
        <Label size='md' weight='bold' id='module__input--label'>
          {module.label}
        </Label>
        <FieldValueInput
          value={fieldValue}
          fieldSchema={fieldSchema}
          caseDataManager={caseDataManager}
          aria-labelledby='module__input--label'
          onChange={setFieldValue}
          onBlur={handleOnBlur}
        />
      </Card.Body>
    </Card>
  );
};

const FieldValueInput = ({
  value,
  fieldSchema,
  onChange,
  onBlur,
  'aria-labelledby': labelledBy,
}: {
  value: unknown;
  fieldSchema: FieldSchema | undefined;
  caseDataManager: CaseDataManager;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  'aria-labelledby': string;
}) => {
  const [error, setError] = useState<string | undefined>(undefined);

  const onBlurWithError = () => {
    error == null && onBlur();
  };

  const fieldSchemaType = fieldSchema?.type;

  const validateMinMax = ({
    nextVal,
    min,
    max,
  }: {
    nextVal: number;
    min: number | undefined;
    max: number | undefined;
  }) => {
    if ((min && nextVal < min) || (max && nextVal > max)) {
      if (min != null && max != null) {
        return `Value must be between ${min} and ${max}.`;
      } else if (min) {
        return `Value must be at least ${min}.`;
      } else if (max) {
        return `Value must be at most ${max}.`;
      }
    }
  };

  switch (fieldSchemaType) {
    case FieldSchemaType.String: {
      return (
        <InputComponent
          value={value as string}
          type='text'
          aria-labelledby={labelledBy}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlurWithError}
        />
      );
    }
    case FieldSchemaType.Number: {
      const min = fieldSchema.config.min;
      const max = fieldSchema.config.max;
      return (
        <>
          <InputComponent
            value={value as number}
            type='number'
            aria-labelledby={labelledBy}
            onChange={(e) => {
              const nextVal = Number(e.target.value);
              setError(validateMinMax({ nextVal, min, max }));
              onChange(nextVal);
            }}
            onBlur={onBlurWithError}
          />
          {error && <FormError>{error}</FormError>}
        </>
      );
    }
    case FieldSchemaType.Integer: {
      const min = fieldSchema.config.min;
      const max = fieldSchema.config.max;
      return (
        <>
          <InputComponent
            value={value as number}
            type='number'
            aria-labelledby={labelledBy}
            onChange={(e) => {
              const nextVal = Number(e.target.value);
              if (Math.floor(nextVal) !== nextVal) {
                setError('Must input an integer.');
              } else {
                setError(validateMinMax({ nextVal, min, max }));
              }

              onChange(nextVal);
            }}
            onBlur={onBlurWithError}
          />
          {error && <FormError>{error}</FormError>}
        </>
      );
    }
    case FieldSchemaType.Date: {
      const dateValue = value ? parseDate(value as string) : null;
      return (
        <DatePicker
          value={dateValue}
          aria-labelledby={labelledBy}
          onChange={(nextDate) => {
            onChange(nextDate?.toString() ?? null);
          }}
          onBlur={onBlurWithError}
        />
      );
    }
    default:
      return (
        <Paragraph color='secondary'>
          Updating this field type is not supported by this module.
        </Paragraph>
      );
  }
};

export default Input;
